import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, of, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ErrorHandlerService } from '../../services/error-handler/error-handler.base-service';
import { ResidentFeedbackFacade } from '../../services/feedback';
import { LoadPersonalDataSuccess } from '../account';
import * as fromActions from './resident-feedback.actions';

/**
 * Effects for the ART-19551 in-app feedback prompt.
 *
 * Named `ResidentFeedbackEffects`, not `FeedbackEffects`: that name is already
 * taken by the toast/snackbar effects in `+state/general/feedback`, and both are
 * re-exported from `@resident-nx/shared`.
 */
@Injectable()
export class ResidentFeedbackEffects {
  private readonly actions$ = inject(Actions);
  private readonly residentFeedbackFacade = inject(ResidentFeedbackFacade);
  private readonly errorHandlerService = inject(ErrorHandlerService);

  loadEligibility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadResidentFeedbackEligibility),
      switchMap(() =>
        this.residentFeedbackFacade.getEligibility().pipe(
          map(eligibility => fromActions.LoadResidentFeedbackEligibilitySuccess({ eligibility })),
          catchError(error => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on resident-feedback.effects.ts:loadEligibility$:',
              error
            );
            return [fromActions.LoadResidentFeedbackEligibilityFailed({ error })];
          })
        )
      )
    )
  );

  /**
   * Personal data loading is the closest thing this app has to a "the resident is
   * now authenticated" signal, and it fires on fresh login as well as on resume.
   * The app shell also dispatches the load at startup and on resume, so between
   * the three the flag is normally in place well before any successful event.
   *
   * The presenter checks the gate synchronously and simply skips if the flag has
   * not arrived yet. That is safe: nothing was recorded, so the next successful
   * event asks. Waiting instead would risk presenting the sheet over whatever
   * page the resident had since navigated to.
   */
  loadEligibilityOnPersonalData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadPersonalDataSuccess),
      switchMap(() => of(fromActions.LoadResidentFeedbackEligibility()))
    )
  );

  // Fire-and-forget by design (spec ED-1/NFR-1): a failure is recorded in the
  // action state but never surfaced. The resident already answered; the BE
  // re-asks in 3 months if this never landed.
  saveFeedback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.SaveResidentFeedback),
      switchMap(({ input }) =>
        this.residentFeedbackFacade.saveFeedback(input).pipe(
          map(saved => {
            // The contract is `true` or a GraphQL error; `false` is not a value the
            // BE returns. Treat one as a failure anyway rather than reporting a
            // success we cannot back up — thrown here so it lands in the
            // catchError below and is logged like any other failed save.
            if (!saved) {
              throw new Error('saveFeedback resolved without a true payload');
            }
            return fromActions.SaveResidentFeedbackSuccess();
          }),
          catchError(error => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on resident-feedback.effects.ts:saveFeedback$:',
              error
            );
            return [fromActions.SaveResidentFeedbackFailed({ error })];
          })
        )
      )
    )
  );
}
